import { type CSSProperties, type ReactElement, type ReactNode, type RefObject, useCallback, useEffect, useId, useRef, useState } from 'react';
import { DURATION, EFFECT_CLASS, Z_INDEX } from '@vello/tokens';
import { UiIcon, type UiIconName } from './icons';
import { Kbd } from './primitives';

/**
 * The web shell's floating layers (Claude Design A08 / FX-03 + FX-08): a popover menu, the right-hand record
 * drawer, the focal confirm dialog, the bottom toast and the ⌘K command palette. Every layer follows the same
 * choreography from the effect tokens — mount, add `in` a frame later, remove `in` then unmount after the slow
 * duration — and closes on its scrim or `Escape`. Colors/shadows are token classes from `theme.css`.
 *
 * Reduced motion (D547): the menu, drawer, dialog, palette and their scrims are LAYER classes (`vello-layer-*`) and keep
 * animating under `prefers-reduced-motion: reduce`, like the design's inline layer transitions; the toast is an FX class
 * (`vello-fx-toast`) and is stilled.
 *
 * Keyboard (D387): the open layers form a stack, and only the TOP one answers `Escape` (a confirm over a drawer closes
 * the confirm, not both). The modal layers — the drawer, the confirm dialog, the palette — move focus inside when they
 * open, keep Tab / Shift+Tab inside while they are on top, and give focus back to the control that opened them when
 * they close (`useModalFocus`).
 */

/** Mount/unmount choreography for a layer: returns whether to render and the class to apply. */
export function useLayer(open: boolean): { mounted: boolean; cls: string } {
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    let t: number | undefined;
    if (open) {
      setMounted(true);
      t = window.setTimeout(() => setEntered(true), 30);
    } else {
      setEntered(false);
      t = window.setTimeout(() => setMounted(false), DURATION.slow);
    }
    return () => {
      if (t !== undefined) window.clearTimeout(t);
    };
  }, [open]);
  return { mounted, cls: entered ? EFFECT_CLASS.in : '' };
}

/** The open layers, oldest first: only the last (top) one answers Escape and keeps focus inside. */
const LAYER_STACK: symbol[] = [];

/** A modal layer's container is `open` or `closing` (its exit choreography still on the page) in this attribute. */
const LAYER_ATTR = 'data-vello-layer';

/** Put a layer on the stack while `open`; returns a stable check "is this layer the top one". */
export function useLayerStack(open: boolean): () => boolean {
  const id = useRef<symbol | undefined>(undefined);
  if (id.current === undefined) id.current = Symbol('vello-layer');
  useEffect(() => {
    if (!open) return;
    const me = id.current as symbol;
    LAYER_STACK.push(me);
    return () => {
      const i = LAYER_STACK.lastIndexOf(me);
      if (i >= 0) LAYER_STACK.splice(i, 1);
    };
  }, [open]);
  return useCallback(() => LAYER_STACK[LAYER_STACK.length - 1] === id.current, []);
}

/**
 * Close on Escape while `open` — only when this layer is the top one of the stack. Pass the `isTop` of a layer that
 * already registered itself with {@link useLayerStack}; without it the hook registers the layer on its own.
 */
export function useEscape(open: boolean, onClose: () => void, isTop?: () => boolean): void {
  const own = useLayerStack(open && isTop === undefined);
  const top = isTop ?? own;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && top()) {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose, top]);
}

const TABBABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(', ');

/** The keyboard-reachable elements inside `root`, in document order (hidden, inert and `tabindex=-1` excluded). */
export function tabbablesIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE)).filter((el) => {
    const explicit = el.getAttribute('tabindex');
    return (explicit === null || Number(explicit) >= 0) && el.closest('[hidden], [inert], [aria-hidden="true"]') === null;
  });
}

/**
 * Modal focus (D387; the WAI-ARIA dialog pattern). While the layer is open:
 *   - when it appears, focus moves to its `[data-autofocus]` element, else its first tabbable, else the layer itself;
 *   - Tab on the last element wraps to the first and Shift+Tab on the first wraps to the last (only while the layer is
 *     the top one, so a confirm over a drawer keeps focus in the confirm);
 *   - when it closes, focus goes back to the element that had it when the layer opened, if that is still on the page and
 *     not inside a layer that is closing too (a confirm whose action also closes the drawer it was asked from: the
 *     drawer gives focus back to ITS opener; the confirm's opener is about to be removed — W5 review).
 * `ref` is the layer's container (give it `tabIndex={-1}` and `data-vello-layer={open ? 'open' : 'closing'}`); `mounted`
 * is the `useLayer` mount flag; `isTop` comes from {@link useLayerStack}.
 */
export function useModalFocus(ref: RefObject<HTMLElement | null>, open: boolean, mounted: boolean, isTop: () => boolean): void {
  const opener = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      wasOpen.current = true;
      const active = document.activeElement;
      opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
    } else if (!open && wasOpen.current) {
      wasOpen.current = false;
      const back = opener.current;
      opener.current = null;
      if (back !== null && back.isConnected && back.closest(`[${LAYER_ATTR}="closing"]`) === null) back.focus();
    }
  }, [open]);
  useEffect(() => {
    if (!open || !mounted) return;
    const root = ref.current;
    if (root === null || root.contains(document.activeElement)) return;
    const first = root.querySelector<HTMLElement>('[data-autofocus]') ?? tabbablesIn(root)[0] ?? root;
    first.focus();
  }, [open, mounted, ref]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab' || !isTop()) return;
      const root = ref.current;
      if (root === null) return;
      const items = tabbablesIn(root);
      const current = document.activeElement;
      if (items.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = items[0] as HTMLElement;
      const last = items[items.length - 1] as HTMLElement;
      if (current === null || !root.contains(current)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && (current === first || current === root)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, ref, isTop]);
}

/**
 * A popover anchored inside a `position: relative` parent; a transparent fixed scrim catches outside clicks. `role`
 * defaults to `menu` (a list of menu items); a popover of other content (the column picker, notifications) passes
 * `dialog` with an `ariaLabel`.
 */
export function Popover({ open, onClose, children, width = 300, align = 'right', top = 48, style, role = 'menu', ariaLabel }: { open: boolean; onClose: () => void; children: ReactNode; width?: number; align?: 'left' | 'right'; top?: number; style?: CSSProperties; role?: 'menu' | 'dialog'; ariaLabel?: string }): ReactElement | null {
  const { mounted, cls } = useLayer(open);
  useEscape(open, onClose);
  if (!mounted) return null;
  return (
    <>
      <div className="vello-scrim vello-scrim--clear" style={{ zIndex: Z_INDEX.overlay }} onClick={onClose} />
      <div className={`vello-menu ${EFFECT_CLASS.pop} ${cls}`} role={role} aria-label={ariaLabel} style={{ position: 'absolute', top, [align]: 0, width, zIndex: Z_INDEX.overlay + 1, ...style }}>
        {children}
      </div>
    </>
  );
}

/** The right-hand record drawer (520px) over a blurred scrim: modal — focus moves in, stays in, and returns (D387). */
export function Drawer({ open, onClose, children, labelledBy }: { open: boolean; onClose: () => void; children: ReactNode; labelledBy?: string }): ReactElement | null {
  const { mounted, cls } = useLayer(open);
  const ref = useRef<HTMLElement>(null);
  const isTop = useLayerStack(open);
  useEscape(open, onClose, isTop);
  useModalFocus(ref, open, mounted, isTop);
  if (!mounted) return null;
  return (
    <>
      <div className={`vello-scrim vello-scrim--drawer ${EFFECT_CLASS.scrim} ${cls}`} style={{ zIndex: Z_INDEX.modal }} onClick={onClose} />
      <aside ref={ref} tabIndex={-1} className={`vello-drawer ${EFFECT_CLASS.panel} ${cls}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy} data-vello-layer={open ? 'open' : 'closing'} style={{ zIndex: Z_INDEX.modal + 1 }}>
        {children}
      </aside>
    </>
  );
}

export interface ConfirmSpec {
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly tone?: 'primary' | 'danger';
  readonly icon?: UiIconName;
  /** Reason chips the operator picks from (the reason is mandatory when present). */
  readonly reasons?: readonly string[];
  readonly reasonLabel?: string;
  readonly notePlaceholder?: string;
  /**
   * A mandatory free-text reason (W1-ADMIN, D358): shows the note field even without reason chips and keeps the confirm
   * disabled until it is non-empty. Governance actions whose reason lands on an audit trail use this.
   */
  readonly requireNote?: boolean;
  /**
   * A word the person must TYPE before the confirm enables (D674: clearing the whole places base asks for "LIMPAR"). Compared ignoring case and surrounding spaces; the typed text is
   * returned as the result's `note`. The field is labelled by `reasonLabel` and placeholder `notePlaceholder`, like the note it replaces.
   */
  readonly requireText?: string;
  /**
   * Reason chips only, no free-text note (W3-MERCHANT-QUEUE, D372): for a decision whose reason is a stable code the
   * server keeps as is — a note field there would collect text that is never sent.
   */
  readonly reasonsOnly?: boolean;
}

export interface ConfirmResult {
  readonly confirmed: boolean;
  readonly reason?: string;
  readonly note?: string;
}

/**
 * The focal confirm dialog (440px): icon, title, body, optional reason chips + note, cancel / confirm. Modal (D387):
 * focus starts on Cancel (the least destructive action), Tab stays inside, Escape cancels, and focus returns to the
 * control that asked for the confirmation.
 */
export function ConfirmDialog({ spec, open, onResolve }: { spec: ConfirmSpec | undefined; open: boolean; onResolve: (r: ConfirmResult) => void }): ReactElement | null {
  const { mounted, cls } = useLayer(open);
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [note, setNote] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bodyId = useId();
  const cancel = useCallback((): void => onResolve({ confirmed: false }), [onResolve]);
  const isTop = useLayerStack(open);
  useEscape(open, cancel, isTop);
  useModalFocus(ref, open, mounted && spec !== undefined, isTop);
  useEffect(() => {
    if (open) {
      setReason(undefined);
      setNote('');
    }
  }, [open]);
  if (!mounted || spec === undefined) return null;
  const danger = spec.tone === 'danger';
  const needsReason = spec.reasons !== undefined && spec.reasons.length > 0;
  const typedWord = spec.requireText;
  const needsNote = spec.requireNote === true || typedWord !== undefined;
  const blocked = (needsReason && reason === undefined) || (needsNote && note.trim() === '') || (typedWord !== undefined && note.trim().toLowerCase() !== typedWord.trim().toLowerCase());
  return (
    <>
      <div className={`vello-scrim ${EFFECT_CLASS.scrim} ${cls}`} style={{ zIndex: Z_INDEX.modal + 2 }} onClick={cancel} />
      <div className="vello-dialog-host" style={{ zIndex: Z_INDEX.modal + 3 }}>
        <div ref={ref} tabIndex={-1} className={`vello-dialog ${EFFECT_CLASS.dialog} ${cls}`} role="alertdialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={bodyId} data-vello-layer={open ? 'open' : 'closing'}>
          <span className="vello-dialog__icon" aria-hidden="true" style={{ background: danger ? 'var(--color-danger-soft)' : 'var(--color-warning-soft)', color: danger ? 'var(--color-danger-text)' : 'var(--color-warning-text)' }}>
            <UiIcon name={spec.icon ?? (danger ? 'ban' : 'pause')} size={22} />
          </span>
          <h2 id={titleId} className="vello-dialog__title">{spec.title}</h2>
          <p id={bodyId} className="vello-dialog__body">{spec.body}</p>
          {(needsReason || needsNote) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
              <span className="vello-dialog__label">{spec.reasonLabel}</span>
              {needsReason && (
                <div className="vello-dialog__chips">
                  {spec.reasons?.map((r) => (
                    <button key={r} type="button" className="vello-chip" aria-pressed={reason === r} onClick={() => setReason(r)}>
                      {r}
                    </button>
                  ))}
                </div>
              )}
              {(spec.reasonsOnly !== true || needsNote) && (
                <input className="vello-input vello-dialog__note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={spec.notePlaceholder} aria-label={spec.notePlaceholder} />
              )}
            </div>
          )}
          <div className="vello-dialog__actions">
            <button type="button" className="vello-btn vello-btn--muted" data-autofocus onClick={cancel}>
              {spec.cancelLabel}
            </button>
            <button
              type="button"
              className={`vello-btn ${danger ? 'vello-btn--danger-solid' : 'vello-btn--primary'}`}
              disabled={blocked}
              onClick={() => onResolve({ confirmed: true, ...(reason !== undefined ? { reason } : {}), ...(note.trim() !== '' ? { note: note.trim() } : {}) })}
            >
              {spec.confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export interface ToastSpec {
  readonly message: string;
  readonly icon?: UiIconName;
  /**
   * `danger` is an error the person must read (D509): it is announced as `role="alert"`, carries no life bar (it does
   * not drain) and the {@link FeedbackProvider} keeps it on screen until it is dismissed or replaced.
   */
  readonly tone?: 'success' | 'warning' | 'neutral' | 'danger';
  readonly undoLabel?: string;
  readonly onUndo?: () => void;
}

/** The bottom-centre toast: navy, 4s life bar (none for `danger`), optional undo. `danger` is `role="alert"`, the rest `status`. */
export function Toast({ spec, open, onClose, closeLabel }: { spec: ToastSpec | undefined; open: boolean; onClose: () => void; closeLabel: string }): ReactElement | null {
  const { mounted, cls } = useLayer(open);
  if (!mounted || spec === undefined) return null;
  const danger = spec.tone === 'danger';
  const iconColor = spec.tone === 'success' ? 'var(--color-success)' : spec.tone === 'warning' ? 'var(--color-warning)' : danger ? 'var(--color-danger)' : 'var(--color-on-inverse-primary)';
  return (
    <div className="vello-toast-host" style={{ zIndex: Z_INDEX.toast }}>
      <div className={`vello-toast ${EFFECT_CLASS.toast} ${cls}`} role={danger ? 'alert' : 'status'} aria-live={danger ? 'assertive' : 'polite'} data-tone={spec.tone}>
        <span style={{ color: iconColor, display: 'inline-flex', flex: 'none' }}>
          <UiIcon name={spec.icon ?? (danger ? 'shieldAlert' : 'check')} size={18} />
        </span>
        <span className="vello-toast__msg">{spec.message}</span>
        {spec.onUndo !== undefined && (
          <button type="button" className="vello-toast__undo" onClick={spec.onUndo}>
            {spec.undoLabel}
          </button>
        )}
        <button type="button" className="vello-toast__close" aria-label={closeLabel} title={closeLabel} onClick={onClose}>
          <UiIcon name="x" size={13} />
        </button>
        {!danger && <span className={`vello-toast__life ${EFFECT_CLASS.life}`} aria-hidden="true" />}
      </div>
    </div>
  );
}

export interface PaletteItem {
  readonly id: string;
  readonly label: string;
  readonly sublabel?: string;
  readonly icon?: UiIconName;
  /** Initials for a person row (renders an avatar instead of an icon). */
  readonly initials?: string;
  readonly kbd?: string;
  readonly onSelect: () => void;
}

export interface PaletteSection {
  readonly id: string;
  readonly title: string;
  readonly items: readonly PaletteItem[];
}

/**
 * The ⌘K palette: search input, grouped results, keyboard navigation (↑↓ ↵ esc), footer hints. The input is a
 * combobox over a listbox of options (the highlighted one is `aria-selected` and the input's active descendant), so
 * no button carries `aria-selected` (D387). Modal: focus starts in the input and returns to the trigger on close.
 */
export function CommandPalette({ open, onClose, query, onQuery, sections, placeholder, emptyText, navigateHint, openHint, countText }: { open: boolean; onClose: () => void; query: string; onQuery: (q: string) => void; sections: readonly PaletteSection[]; placeholder: string; emptyText: string; navigateHint: string; openHint: string; countText: string }): ReactElement | null {
  const { mounted, cls } = useLayer(open);
  const [idx, setIdx] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const baseId = useId();
  const isTop = useLayerStack(open);
  useEscape(open, onClose, isTop);
  useModalFocus(ref, open, mounted, isTop);
  const flat = sections.flatMap((s) => s.items);
  const sel = flat.length > 0 ? Math.min(idx, flat.length - 1) : 0;
  useEffect(() => {
    if (open) setIdx(0);
  }, [open]);
  useEffect(() => setIdx(0), [query]);
  if (!mounted) return null;
  const listId = `${baseId}-list`;
  const optionId = (n: number): string => `${baseId}-opt-${n}`;
  let k = -1;
  return (
    <>
      <div className={`vello-scrim vello-scrim--palette ${EFFECT_CLASS.scrim} ${cls}`} style={{ zIndex: Z_INDEX.modal + 4 }} onClick={onClose} />
      <div className="vello-palette-host" style={{ zIndex: Z_INDEX.modal + 5 }}>
        <div ref={ref} tabIndex={-1} className={`vello-palette ${EFFECT_CLASS.palette} ${cls}`} role="dialog" aria-modal="true" aria-label={placeholder} data-vello-layer={open ? 'open' : 'closing'}>
          <div className="vello-palette__input">
            <UiIcon name="search" size={18} />
            <input
              data-autofocus
              role="combobox"
              aria-expanded={flat.length > 0}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={flat.length > 0 ? optionId(sel) : undefined}
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              placeholder={placeholder}
              aria-label={placeholder}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setIdx((i) => Math.min(i + 1, Math.max(0, flat.length - 1)));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setIdx((i) => Math.max(i - 1, 0));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  flat[sel]?.onSelect();
                }
              }}
            />
            <Kbd>esc</Kbd>
          </div>
          {flat.length > 0 ? (
            <div className="vello-palette__list" id={listId} role="listbox" aria-label={placeholder}>
              {sections.map((s) =>
                s.items.length === 0 ? null : (
                  <div key={s.id} role="group" aria-label={s.title} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div className="vello-menu__heading" aria-hidden="true">
                      {s.title}
                    </div>
                    {s.items.map((it) => {
                      k += 1;
                      const n = k;
                      const active = n === sel;
                      return (
                        <button key={it.id} id={optionId(n)} type="button" role="option" tabIndex={-1} className="vello-menu__item" aria-selected={active} onClick={it.onSelect} onMouseMove={() => n !== sel && setIdx(n)} style={{ gap: 12 }}>
                          {it.initials !== undefined ? (
                            <span className="vello-avatar" style={{ width: 30, height: 30, fontSize: 11 }}>
                              {it.initials}
                            </span>
                          ) : (
                            <span style={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', background: s.id === 'actions' ? 'var(--color-action-primary)' : 'var(--color-surface-subtle)', color: s.id === 'actions' ? 'var(--color-action-on-primary)' : 'var(--color-text-primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                              <UiIcon name={it.icon ?? 'search'} size={15} />
                            </span>
                          )}
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {it.label}
                            {it.sublabel !== undefined && <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)' }}> · {it.sublabel}</span>}
                          </span>
                          {it.kbd !== undefined && (
                            <span className="tnum" style={{ fontSize: 'var(--text-mono-sm-size)', color: 'var(--color-text-disabled)' }}>
                              {it.kbd}
                            </span>
                          )}
                          <span style={{ color: 'var(--color-text-disabled)', opacity: active ? 1 : 0, display: 'inline-flex' }}>
                            <UiIcon name="cornerDownLeft" size={14} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ),
              )}
            </div>
          ) : (
            <div className="vello-palette__list" role="status">
              <div style={{ padding: 28, textAlign: 'center', fontSize: 'var(--text-caption-size)', color: 'var(--color-text-secondary)' }}>{emptyText}</div>
            </div>
          )}
          <div className="vello-palette__foot">
            <span>
              <Kbd>↑↓</Kbd> {navigateHint}
            </span>
            <span>
              <Kbd>↵</Kbd> {openHint}
            </span>
            <span style={{ flex: 1 }} />
            <span className="tnum">{countText}</span>
          </div>
        </div>
      </div>
    </>
  );
}
